import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box/Box';
import SpinnerOverlay from '../../../components/Spinner/Spinner';
import Typography from '@mui/material/Typography/Typography';
import TablePagination from '@mui/material/TablePagination';
import { useDeleteDepartmentLookupsMutation, useGetDepartmentLookupsQuery, useInsertDepartmentLookupMutation, useUpdateDepartmentLookupMutation, useLazyExportDepartmentLookupsQuery, useBatchReplaceDepartmentLookupMutation } from '../../../services/appSlice/appSlice';
import { DepartmentLookup, DepartmentLookupCreateBody, DepartmentLookupUpdateBody } from '../../../../shared/types/DepartmentLookup';
import ModalWidget from '../../../components/ModalWidget/ModalWidget';
import { ActionButtons } from '../../../components/CancelSave';
import HoveredIconConfirm from '../../../components/Admin/HoveredIconConfirmButton';
import HoveredIcon from '../../../components/Admin/HoveredIconButton';
import { InputLabel, Stack } from '@mui/material';
import UserSearchBar from '../../../components/TaskOverview/AutoComplete';
import SearchIcon from '@mui/icons-material/Search';
import FindInTable, { FindMatch, FindInTableColumn } from '../../../components/FindInTable';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const textFieldSx = {
  // color: '#222222',
  '& > p': {
    margin: '5px 0px',
    color: '#646669',
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const emailFields = [
  {
    key: 'SolutionArchitectEmail',
    label: 'Solution Architect Email',
    placeholder: 'Ex. SolutionArchitect@constellation.com',
    refName: 'SolutionArchitectEmailRef',
    editFlag: 'isSolutionArchitectEmailEdit',
    type: 'SolutionArchitectEmail',
  },
  {
    key: 'ITKeyManagerEmail',
    label: 'IT Key Manager Email',
    placeholder: 'Ex. it.architecture@constellation.com',
    refName: 'ITKeyManagerEmailRef',
    editFlag: 'isITKeyManagerEmailEdit',
    type: 'ITKeyManagerEmail',
  },
  {
    key: 'VPEmail',
    label: 'VP Email',
    placeholder: 'Ex. vp.architecture@constellation.com',
    refName: 'VPEmailRef',
    editFlag: 'isVPEmailEdit',
    type: 'VPEmail',
  },
  {
    key: 'enterpriceArchitectEmail',
    label: 'Enterprice Architect Email',
    placeholder: 'Ex. enterprice.architecture@constellation.com',
    refName: 'enterpriceArchitectEmailRef',
    editFlag: 'isEnterpriceArchitectEmailEdit',
    type: 'enterpriceArchitectEmail',
  },
];

export default function DepartmentLookupTable() {
  const {
    data: departmentLookups,
    isError: errorDepartmentLookup,
    isLoading: loadingDepartmentLookup,
    refetch
  } = useGetDepartmentLookupsQuery();

  const [deleteDepartmentLookups] = useDeleteDepartmentLookupsMutation();
  const [insertDepartmentLookup] = useInsertDepartmentLookupMutation();
  const [updateDepartmentLookup] = useUpdateDepartmentLookupMutation();
  const [batchReplaceDepartmentLookup] = useBatchReplaceDepartmentLookupMutation();
  const [exportDepartmentLookups, { data: exportFileUrl, isSuccess: isExportSuccess }] = useLazyExportDepartmentLookupsQuery();

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const refs = {
    SolutionArchitectEmailRef: useRef(null),
    ITKeyManagerEmailRef: useRef(null),
    VPEmailRef: useRef(null),
    enterpriceArchitectEmailRef: useRef(null),
  };
  const [form, setForm] = useState({
    id: 0,
    Organization: '',
    VPEmail: '',
    ITKeyManagerEmail: '',
    SolutionArchitectEmail: '',
    enterpriceArchitectEmail: ''
  });

const [editFlags, setEditFlags] = useState({
  isSolutionArchitectEmailEdit: false,
  isITKeyManagerEmailEdit: false,
  isVPEmailEdit: false,
  isEnterpriceArchitectEmailEdit: false,
});

const openEdit = (editFlag: string) => {
  setEditFlags({
    isSolutionArchitectEmailEdit: false,
    isITKeyManagerEmailEdit: false,
    isVPEmailEdit: false,
    isEnterpriceArchitectEmailEdit: false,
    [editFlag]: true,
  });
};

const closeEdit = () => {
  setEditFlags({
    isSolutionArchitectEmailEdit: false,
    isITKeyManagerEmailEdit: false,
    isVPEmailEdit: false,
    isEnterpriceArchitectEmailEdit: false,
  });
};

const handleChangeUser = (type: string, user: any) => {
  const field = emailFields.find((f) => f.type === type);
  if (field) {
    setForm((prev) => ({
      ...prev,
      [field.key]: user?.email || "",
    }));
  }
  closeEdit();
};


  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Search state
  const [search, setSearch] = useState('');

  // Find feature state
  const [findModalOpen, setFindModalOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState<FindMatch | null>(null);

  // Columns for find feature
  const findColumns: FindInTableColumn[] = [
    { key: 'Organization', label: 'Organization' },
    { key: 'SolutionArchitectEmail', label: 'Solution Architect Email' },
    { key: 'ITKeyManagerEmail', label: 'IT Key Manager Email' },
    { key: 'VPEmail', label: 'VP Email' },
    { key: 'enterpriceArchitectEmail', label: 'Enterprise Architect Email' }
  ];

  // List of organization names already present in the table (case-insensitive)
  const usedOrganizations = useMemo(
    () =>
      (departmentLookups || []).map((row) =>
        row.Organization ? row.Organization.trim().toLowerCase() : ''
      ),
    [departmentLookups]
  );


  // Validate emails on change
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Disable Add button if organization is duplicate or empty
  const isDuplicateOrganization = (org: string) =>
    usedOrganizations.includes(org.trim().toLowerCase());

  const handleOpen = () => {
    setIsEdit(false);
    setForm({
      id: 0,
      Organization: '',
      VPEmail: '',
      ITKeyManagerEmail: '',
      SolutionArchitectEmail: '',
      enterpriceArchitectEmail: ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setForm({
      id: 0,
      Organization: '',
      VPEmail: '',
      ITKeyManagerEmail: '',
      SolutionArchitectEmail: '',
      enterpriceArchitectEmail: ''
    });
  };

  const handleEdit = (row: DepartmentLookup) => {
    setIsEdit(true);
    setForm({
      id: row.id,
      Organization: row.Organization || '',
      VPEmail: row.VPEmail || '',
      ITKeyManagerEmail: row.ITKeyManagerEmail || '',
      SolutionArchitectEmail: row.SolutionArchitectEmail || '',
      enterpriceArchitectEmail: row.enterpriceArchitectEmail || ''
    });
    setOpen(true);
  };

  const handleDeleteConfirm = async (row: DepartmentLookup) => {
    await deleteDepartmentLookups(row.id);
    refetch();
  };

  const handleAddOrUpdate = async () => {
    // Prevent duplicate organization on add
    if (
      !isEdit
    ) {
      if (isDuplicateOrganization(form.Organization)) {
        alert('Organization already exists in the table!');
        return;
      } else {
        const addDepartmentObj: DepartmentLookupCreateBody = {
          Organization: form.Organization,
          VPEmail: form.VPEmail,
          ITKeyManagerEmail: form.ITKeyManagerEmail,
          SolutionArchitectEmail: form.SolutionArchitectEmail,
          enterpriceArchitectEmail: form.enterpriceArchitectEmail
        }
        await insertDepartmentLookup(addDepartmentObj);
        refetch()
      }
    } else {
      const updateDepartmentObj: DepartmentLookupUpdateBody = {
        Organization: form.Organization,
        VPEmail: form.VPEmail,
        ITKeyManagerEmail: form.ITKeyManagerEmail,
        SolutionArchitectEmail: form.SolutionArchitectEmail,
        enterpriceArchitectEmail: form.enterpriceArchitectEmail
      }
      await updateDepartmentLookup({ id: form.id, body: updateDepartmentObj });
      refetch();
    }

    handleClose();
  };

  // Initialize local data when departmentLookups changes
  useEffect(() => {
    if (isExportSuccess && exportFileUrl) {
      const link = document.createElement('a');
      link.href = exportFileUrl;
      link.setAttribute('download', 'department-lookups.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [isExportSuccess, exportFileUrl]);

  const handleExportToExcel = () => {
    exportDepartmentLookups();
  };

  // --- Pagination and Search logic ---
  // Filter by search
  const filteredRows = useMemo(() => {
    if (!departmentLookups) return [];
    if (!search) return departmentLookups;
    const lower = search.toLowerCase();
    return departmentLookups.filter(row =>
      (row.Organization || '').toLowerCase().includes(lower) ||
      (row.VPEmail || '').toLowerCase().includes(lower) ||
      (row.ITKeyManagerEmail || '').toLowerCase().includes(lower) ||
      (row.SolutionArchitectEmail || '').toLowerCase().includes(lower) ||
      (row.enterpriceArchitectEmail || '').toLowerCase().includes(lower)
    );
  }, [departmentLookups, search]);

  // Paginate
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // Reset page if search/filter changes and page is out of bounds
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredRows.length) {
      setPage(0);
    }
  }, [filteredRows, page, rowsPerPage]);

  // Find functionality handlers
  const handleOpenFind = () => {
    setFindModalOpen(true);
  };

  const handleMatchFound = (match: FindMatch) => {
    const rowIndex = filteredRows.findIndex(row => row.id === match.rowId);
    if (rowIndex !== -1) {
      const targetPage = Math.floor(rowIndex / rowsPerPage);
      setPage(targetPage);
    }
    setCurrentMatch(match);
  };

  const isCurrentMatch = (rowId: number, column: string) => {
    if (!currentMatch) return false;
    return currentMatch.rowId === rowId && currentMatch.column === column;
  };

  const handleReplace = async (rowId: number, column: string, newValue: string) => {
    try {
      const row = departmentLookups?.find(r => r.id === rowId);
      if (!row) return;
      
      const updateBody: DepartmentLookupUpdateBody = {
        Organization: row.Organization,
        VPEmail: row.VPEmail,
        ITKeyManagerEmail: row.ITKeyManagerEmail,
        SolutionArchitectEmail: row.SolutionArchitectEmail,
        enterpriceArchitectEmail: row.enterpriceArchitectEmail,
        [column]: newValue
      };
      
      await updateDepartmentLookup({ id: rowId, body: updateBody });
      await refetch();
    } catch (error) {
      console.error('Error updating row:', error);
      alert('Error updating data. Please try again.');
    }
  };

  const handleReplaceAll = async (replacements: Array<{ rowId: number; column: string; newValue: string }>) => {
    try {
      const formattedReplacements = replacements.map(({ rowId, column, newValue }) => ({
        id: rowId,
        column,
        newValue,
      }));
      
      await batchReplaceDepartmentLookup({ replacements: formattedReplacements });
      await refetch();
    } catch (error) {
      console.error('Error updating rows:', error);
    }
  };

  if (loadingDepartmentLookup) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
        py="24px"
      >
        <SpinnerOverlay />
      </Box>
    );
  }

  if (errorDepartmentLookup) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
        py="24px"
      >
        <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>
          Error
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Search, Find and Export */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleOpenFind}
          sx={{ textTransform: 'none' }}
        >
          Find
        </Button>
        <Button
          variant="contained"
          startIcon={<i className="fa-solid fa-file-excel"></i>}
          sx={{
            backgroundColor: '#217346',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#1a5c37',
            }
          }}
          onClick={handleExportToExcel}
        >
          Export to Excel
        </Button>
        <TextField
          size="small"
          placeholder="Search ( Organization, Emails )"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 450 }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ height: 'fit-content', maxHeight: '70vh' }}>
        <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: '32%' }}>Organization</StyledTableCell>
              
              
              <StyledTableCell sx={{ width: '14%', textWrap:'nowrap' }}>Solution Architect Email</StyledTableCell>
              <StyledTableCell sx={{ width: '14%', textWrap:'nowrap' }}>IT Key Manager Email</StyledTableCell>
              <StyledTableCell sx={{ width: '14%', textWrap:'nowrap' }}>VP Email</StyledTableCell>
              <StyledTableCell sx={{ width: '14%', textWrap:'nowrap' }}>Enterprise Architect Email</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: '12%', textWrap:'nowrap' }}>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length ? paginatedRows.map((row) => (
              <StyledTableRow key={row.id}>
                <StyledTableCell sx={{ 
                  width: '32%', 
                  textWrap:'nowrap',
                  backgroundColor: isCurrentMatch(row.id, 'Organization') ? '#ffeb3b' : 'inherit'
                }}>
                  {row.Organization}
                </StyledTableCell>
                <StyledTableCell sx={{ 
                  width: '14%', 
                  textWrap:'nowrap',
                  backgroundColor: isCurrentMatch(row.id, 'SolutionArchitectEmail') ? '#ffeb3b' : 'inherit'
                }}>
                  {row.SolutionArchitectEmail || '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ 
                  width: '14%', 
                  textWrap:'nowrap',
                  backgroundColor: isCurrentMatch(row.id, 'ITKeyManagerEmail') ? '#ffeb3b' : 'inherit'
                }}>
                  {row.ITKeyManagerEmail || '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ 
                  width: '14%', 
                  textWrap:'nowrap',
                  backgroundColor: isCurrentMatch(row.id, 'VPEmail') ? '#ffeb3b' : 'inherit'
                }}>
                  {row.VPEmail || '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ 
                  width: '14%', 
                  textWrap:'nowrap',
                  backgroundColor: isCurrentMatch(row.id, 'enterpriceArchitectEmail') ? '#ffeb3b' : 'inherit'
                }}>
                  {row.enterpriceArchitectEmail || '-'}
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' , textWrap:'nowrap'}}>
                  <Box component="div" sx={{ display: 'inline', mx: 1 }}>
                    <HoveredIconConfirm
                      icon="fa-regular fa-trash"
                      hoverIcon="fa-solid fa-trash"
                      title='Confirm Organization Delete'
                      content={`Are you sure you want to delete record for ${row.Organization} ?`}
                      onClick={() => handleDeleteConfirm(row)}
                    />
                  </Box>
                  <Box component="div" sx={{ display: 'inline', mx: 1 }}>
                    <HoveredIcon
                      icon="fa-regular fa-pencil"
                      hoverIcon="fa-solid fa-pencil"
                      onClick={() => handleEdit(row)}
                    />
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            )) :
              <Typography color="error.main" fontSize={typographyTokens.fontSizes.xl} padding="17px 0PX 0px 20px">
                No record found
              </Typography>
            }
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{
        margin: '16px 0px 10px 0px',
      }}>
        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            variant="contained"
            sx={{
              margin: '16px 0px 10px 0px',
              backgroundColor: '#2372B9',
              textTransform: 'none',
            }}
            onClick={handleOpen}
          >
            Add Organization
          </Button>
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <ModalWidget
        isOpen={open}
        onClose={handleClose}
        title={isEdit ? 'Edit Organization' : 'Add Organization'}
        children={
          <><DialogContent>
            <Box sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  // color: '#222222',
                  mb: 1
                }}
                htmlFor="departmentlookup-organization"
              >
                Organization
              </InputLabel>
              <TextField
                sx={textFieldSx}
                size="small"
                id="departmentlookup-organization"
                data-testid="departmentlookup-organization"
                placeholder="Ex. 09341 - GEN Architecture"
                name="Organization"
                fullWidth
                value={form.Organization}
                onChange={handleChange}
                disabled={isEdit}
                error={
                  !isEdit &&
                  !!form.Organization &&
                  isDuplicateOrganization(form.Organization)
                }
                helperText={
                  !isEdit &&
                    !!form.Organization &&
                    isDuplicateOrganization(form.Organization)
                    ? 'Organization already exists'
                    : ''
                }
              />
            </Box>

       {emailFields.map((field) => {
                const refKey = field.refName as keyof typeof refs;
                const formKey = field.key as keyof typeof form;
                const editFlagKey = field.editFlag as keyof typeof editFlags;

                return (
                  <Box sx={{ mb: 2 }} ref={refs[refKey]} key={field.key}>
                    <InputLabel
                      sx={{ mb: 1 }}
                      htmlFor={`departmentlookup-${field.key}`}
                    >
                      {field.label}
                    </InputLabel>
                    <TextField
                      sx={textFieldSx}
                      size="small"
                      fullWidth
                      value={form[formKey]}
                      onClick={() => openEdit(field.editFlag)}
                      placeholder={field.placeholder}
                      name={field.key}
                    />
                    <UserSearchBar
                      type={field.type}
                      show={editFlags[editFlagKey]}
                      anchor={refs[refKey].current}
                      onClose={closeEdit}
                      onChange={(user: any) =>
                        handleChangeUser(field.type, user)
                      }
                    />
                  </Box>
                );
              })}
            
          </DialogContent>
            <ActionButtons
              handleCancel={handleClose}
              handleSave={handleAddOrUpdate}
              buttonValidation={
                !form.Organization ||
                (!isEdit && isDuplicateOrganization(form.Organization))
              }
              saveLabel={isEdit ? 'Update' : 'Add'}
            />
          </>
        }
      />

      {/* Find Modal */}
      <FindInTable<DepartmentLookup>
        data={filteredRows}
        columns={findColumns}
        isOpen={findModalOpen}
        onClose={() => {
          setFindModalOpen(false);
          setCurrentMatch(null);
        }}
        onMatchFound={handleMatchFound}
        getRowId={(row) => row.id}
        onReplace={handleReplace}
        onReplaceAll={handleReplaceAll}
      />
    </>
  );
}
